import React from 'react';
import { useSelector } from 'react-redux';
import { RouteItem } from 'gql/autogen/RouteItem';
import { Box, Typography, Accordion } from '@drone-js/core';
import { Location } from 'gql/autogen/Location';
import { mapDescription, mapTitle } from 'gql/service/graph/activityService';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import RouteItemHeader from 'component/page/detail/Itineraries/HopOnHopOffItinerary/RouteItem/RouteItemHeader';
import RouteItemSubHeader from 'component/page/detail/Itineraries/HopOnHopOffItinerary/RouteItem/RouteItemSubHeader';
import RouteItemStops from 'component/page/detail/Itineraries/HopOnHopOffItinerary/RouteItem/RouteItemStops';
import RouteItemPoi from 'component/page/detail/Itineraries/HopOnHopOffItinerary/RouteItem/RouteItemPoi';
import getDurationText from 'core/utils/getDurationText';
import { TypographyWhitespacePreWrap } from 'component/page/detail/Itineraries/StructuredItinerary/ItineraryStep';

interface Props {
    routeItem: RouteItem;
    locations: Location[];
    contentLocale: string;
}

const RouteItemComponent: React.FC<Props> = (props) => {
    const { routeItem, locations, contentLocale } = props;
    const cms = useSelector(selectCmsFromBootstrap);
    const duration = routeItem.duration ? `${cms[130820]} ${getDurationText(routeItem.duration, cms)}` : undefined;
    const description = mapDescription(routeItem.description);
    const validStops = routeItem.stops?.filter((stop) => locations.some((loc) => stop.locationRefId === loc.id && loc.name));
    const validPoi = routeItem.pointsOfInterest?.filter((poi) => locations.some((loc) => poi.locationRefId === loc.id && loc.name));

    return (
        <Accordion
            actionType="chevron-icon"
            data-testid="route-accordion"
            aria-label={mapTitle(routeItem.description)}
            lang={contentLocale}
            level={2}
            contentSummary={
                <Box textAlign="start">
                    <RouteItemHeader heading={mapTitle(routeItem.description)} />
                    <RouteItemSubHeader noOfStops={validStops?.length} noOfPointInterest={validPoi?.length} cms={cms} />
                </Box>
            }
        >
            <Typography mx="4" variant="caption-default-strong" tag="h3" textColor="base.subtle">
                {duration}
            </Typography>
            {!!description && (
                <TypographyWhitespacePreWrap mx="4" mb="8" variant="caption-default" tag="span" textColor="base.subtle" lang={contentLocale}>
                    {description}
                </TypographyWhitespacePreWrap>
            )}
            <RouteItemStops stops={validStops} locations={locations} />
            <RouteItemPoi pointsOfInterest={validPoi} locations={locations} />
        </Accordion>
    );
};
RouteItemComponent.displayName = 'RouteItemComponent';

export default RouteItemComponent;
